'use client'

import { useCallback } from 'react'
import { cn } from '@/lib/utils'

interface MartiniLoaderProps {
  className?: string
  size?: number
  /** What is loading, rendered under the mark. Plain status copy, e.g. "Checking your session...". */
  label?: string
}

// Keep in sync with `--ml-dur` (2.2s) on `.martini-loader-stage` in globals.css.
const ML_DURATION_MS = 2200

// The glass path is the first `<path>` of `public/whitemartini.svg`, verbatim:
// outer outline plus the inner triangle cut-out (the two subpaths wind in
// opposite directions, so the default nonzero rule already renders the hole).
// The olive — the second path of that file, a circle at (951.4, 99.2) r≈24.6 —
// stays a CSS circle so it can bounce independently; its position lives in the
// `--ml-olive-*` variables on `.martini-loader-stage`.
const GLASS_VIEW_BOX = '874.65 -64.79 434.22 434.22'
const GLASS_PATH =
  'M1128.76 -7.51 L939.88 167.17 L1127.23 201.38 L1179.33 257.72 L1144.29 290.13 L1164.66 312.16 L1256.77 226.96 L1236.40 204.94 L1201.36 237.35 L1149.13 180.88ZM1117.69 169.15 L1099.40 165.81 L1004.31 148.44 L1105.35 55.01Z'

export function MartiniLoader({ className, size = 72, label }: MartiniLoaderProps) {
  // Phase-lock both nods to a shared wall clock so a loader that mounts as
  // another unmounts — e.g. the boot screen handing off to the boot→canvas
  // crossfade overlay — continues the motion instead of snapping back to frame
  // 0, which reads as a jump. A negative `animation-delay` means "already N ms
  // into the loop"; deriving it from `performance.now()` makes every instance
  // show the same frame at the same instant, so handoffs are seamless.
  //
  // Applied via a ref callback (not render) so the clock read stays out of the
  // pure render pass and off the server: the callback runs on the client during
  // commit, before the first paint, so there is no visible snap and no
  // hydration mismatch.
  const applyNodPhase = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    el.style.animationDelay = `${-((globalThis.performance?.now() ?? 0) % ML_DURATION_MS)}ms`
  }, [])

  const mark = (
    <div
      className={cn('martini-loader-stage', label === undefined && className)}
      style={{ width: size, height: size }}
      role="img"
      // eslint-disable-next-line i18next/no-literal-string -- brand
      aria-label="Martini"
    >
      <div className="martini-loader-mark">
        <div className="martini-loader-glass-wrap" ref={applyNodPhase}>
          <svg
            viewBox={GLASS_VIEW_BOX}
            className="martini-loader-glass"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d={GLASS_PATH} fill="currentColor" />
          </svg>
        </div>
        <div className="martini-loader-olive" ref={applyNodPhase} aria-hidden="true" />
      </div>
    </div>
  )

  if (label === undefined) return mark

  return (
    <div className={cn('flex flex-col items-center', className)}>
      {mark}
      <p className="mt-4 text-center text-[13px] text-muted-foreground" aria-live="polite">
        {label}
      </p>
    </div>
  )
}
